import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';

/**
 * Reads selling-suspension state from the current user's Firestore doc.
 *
 * Field paths (all nested under users/{uid}.billingStatus):
 *   - sellingDisabled: boolean — true when monitor has flipped suspension
 *   - sellingDisabledReason: string — human-readable reason
 *   - sellingDisabledAt: Timestamp — when suspension was applied
 *   - sellingDisabledBillingId: string — platformBilling doc id that triggered it
 *   - outstandingInvoiceCount: number — count of unpaid recovery invoices
 *   - outstandingAmountCents: number — total unpaid in cents
 *
 * Source of truth: functions/billingSuspensionMonitor.js (suspension flips)
 * and functions/createPlatformInvoice.js (aggregate increments).
 *
 * Returns { loading: true } until the UserContext snapshot has loaded so
 * callers can avoid flashing a banner during initial auth resolution.
 */
export function useSellingDisabled() {
  const { firestoreUser, loading } = useContext(UserContext);

  if (loading || !firestoreUser) {
    return {
      loading: true,
      disabled: false,
      reason: null,
      billingId: null,
      disabledAt: null,
      outstandingCount: 0,
      outstandingAmountCents: 0,
    };
  }

  const bs = firestoreUser.billingStatus || {};
  return {
    loading: false,
    disabled: bs.sellingDisabled === true,
    reason: bs.sellingDisabledReason || null,
    billingId: bs.sellingDisabledBillingId || null,
    disabledAt: bs.sellingDisabledAt || null,
    outstandingCount: bs.outstandingInvoiceCount || 0,
    outstandingAmountCents: bs.outstandingAmountCents || 0,
  };
}
